import { useEffect, useLayoutEffect, useRef } from 'react';
import * as Y from 'yjs';
import type { Shape } from '@frame/shared';
import { updateShape } from './binding/shapesMap';
import { effectiveTextStyle } from './textStyle';
import { stickyFitFontSize } from './ShapeRenderer';
import { useTokens } from '../styles/theme';
import { syncTextareaValue } from './textareaSync';

// DOM <textarea> mounted on top of the Konva stage so the user can type
// directly into a shape. We can't edit text inside Konva's <canvas> — it's
// pixels, not DOM. The textarea is positioned in screen coordinates using
// the same viewport transform the stage uses, so it follows the shape when
// the user pans or zooms (though we lock interaction during edit, so pan
// is effectively suspended).
//
// Writes flow per keystroke through `updateShape` with LOCAL_ORIGIN_EDIT;
// the undo manager's captureTimeout collapses bursts of typing into one
// undo step. Peers see typing live via Yjs.
//
// Frame labels (`shape.label`) and text shapes (`shape.text`) use the same
// component; the `field` prop tells it which key to write.
//
// Text style mirroring — every formatting attribute (font size, bold,
// italic, underline, color, alignment) is derived from the live shape's
// `textStyle` so what the user types looks identical to the rendered shape
// behind it. The toolbar (TextFormatToolbar) writes the same textStyle map,
// which means flipping bold mid-edit immediately bolds the textarea too.

export interface TextEditorProps {
  doc: Y.Doc;
  shape: Shape;
  viewport: { x: number; y: number; scale: number };
  onDone: () => void;
}

interface Geometry {
  left: number;
  top: number;
  width: number;
  height: number;
  // Multiplier — applied AFTER textStyle.fontSize so the textarea zooms
  // with the viewport. Konva text scales the same way.
  fontScale: number;
  paddingTop: number;
  paddingLeft: number;
  field: 'text' | 'label';
}

export function TextEditor({ doc, shape, viewport, onDone }: TextEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isFirstSync = useRef(true);
  const tokens = useTokens();
  const style = effectiveTextStyle(shape, tokens);
  const geom = computeGeometry(shape, viewport);
  const initialValue = readCurrentText(shape, geom.field);
  // Stickies shrink the live caret font when typed content would overflow
  // the box (same algorithm the post-blur Konva render uses). Other shape
  // types keep their declared fontSize.
  const effectiveFontSize =
    shape.type === 'sticky'
      ? stickyFitFontSize(shape, style)
      : style.fontSize;

  // On mount, seed the textarea with the current shape text, focus, and put
  // the caret at the end. On every subsequent render (each keystroke round-
  // trips through Yjs and re-renders us), only reassign the value if it
  // genuinely diverged — otherwise we'd reset the caret to the end and the
  // user's mid-string typing would land out of order. See textareaSync.ts.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    if (isFirstSync.current) {
      el.value = initialValue;
      el.focus();
      el.setSelectionRange(initialValue.length, initialValue.length);
      isFirstSync.current = false;
      return;
    }
    syncTextareaValue(el, initialValue);
  }, [initialValue]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    const onInput = () => {
      updateShape(doc, shape.id, { [geom.field]: el.value });
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onDone();
        return;
      }
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onDone();
        return;
      }
    };
    el.addEventListener('input', onInput);
    el.addEventListener('keydown', onKeyDown);
    return () => {
      el.removeEventListener('input', onInput);
      el.removeEventListener('keydown', onKeyDown);
    };
  }, [doc, shape.id, geom.field, onDone]);

  return (
    <textarea
      ref={textareaRef}
      onBlur={onDone}
      spellCheck
      style={{
        position: 'absolute',
        left: geom.left,
        top: geom.top,
        width: geom.width,
        height: geom.height,
        paddingTop: geom.paddingTop,
        paddingLeft: geom.paddingLeft,
        paddingRight: geom.paddingLeft,
        paddingBottom: geom.paddingTop,
        margin: 0,
        border: 'none',
        outline: 'none',
        resize: 'none',
        background: 'transparent',
        color: style.color,
        fontFamily: 'Inter, sans-serif',
        fontSize: effectiveFontSize * geom.fontScale,
        lineHeight: 1.4,
        fontWeight: style.bold ? 700 : 400,
        fontStyle: style.italic ? 'italic' : 'normal',
        textDecoration: style.underline ? 'underline' : 'none',
        textAlign: style.align,
        boxSizing: 'border-box',
        zIndex: 5,
        // verticalAlign on a textarea has no native equivalent — the caret
        // sits at top. The post-blur Konva render honors verticalAlign, so
        // typing renders top-aligned but commits to the final position.
      }}
    />
  );
}

function readCurrentText(shape: Shape, field: 'text' | 'label'): string {
  if (field === 'label' && shape.type === 'frame') return shape.label ?? '';
  if (field === 'text' && 'text' in shape && typeof shape.text === 'string') return shape.text;
  return '';
}

function computeGeometry(
  shape: Shape,
  viewport: { x: number; y: number; scale: number },
): Geometry {
  const { x: vx, y: vy, scale } = viewport;
  const toScreen = (wx: number, wy: number) => ({
    left: vx + wx * scale,
    top: vy + wy * scale,
  });

  // Match the per-shape paddings used by ShapeRenderer.tsx so the textarea
  // visually sits where the rendered Konva text sits.
  switch (shape.type) {
    case 'sticky': {
      const pos = toScreen(shape.x + 12, shape.y + 12);
      return {
        ...pos,
        width: (shape.w - 24) * scale,
        height: (shape.h - 22) * scale,
        fontScale: scale,
        paddingTop: 0,
        paddingLeft: 0,
        field: 'text',
      };
    }
    case 'rect':
    case 'ellipse': {
      const pos = toScreen(shape.x, shape.y);
      return {
        ...pos,
        width: shape.w * scale,
        height: shape.h * scale,
        fontScale: scale,
        paddingTop: 4,
        paddingLeft: 4,
        field: 'text',
      };
    }
    case 'text': {
      const pos = toScreen(shape.x, shape.y);
      // Width tracks a comfortable max for first-time typing; height grows
      // to fit the cursor at the current font size.
      const probeFontSize = shape.fontSize ?? 16;
      return {
        ...pos,
        width: 280 * scale,
        height: (probeFontSize + 16) * scale,
        fontScale: scale,
        paddingTop: 2,
        paddingLeft: 2,
        field: 'text',
      };
    }
    case 'frame': {
      const pos = toScreen(shape.x, shape.y - 18);
      return {
        ...pos,
        width: shape.w * scale,
        height: 18 * scale,
        fontScale: scale,
        paddingTop: 0,
        paddingLeft: 0,
        field: 'label',
      };
    }
    default: {
      return {
        left: -9999, top: -9999,
        width: 0, height: 0,
        fontScale: 1,
        paddingTop: 0,
        paddingLeft: 0,
        field: 'text',
      };
    }
  }
}
